import React from "react";
import type { DateRange } from "@daypicker/react";
import { useConstant } from "@hooks/useConstant";
import type { PeriodSelectorProps } from "./DatePicker.types";
import {
    areDateOnlyRangesEqual,
    DEFAULT_INPUT_FORMATS,
    formatDateRange,
    getDefaultRangePresets,
    getMonthRange,
    getQuarterRange,
    getYearRange,
    parseDateOnly,
    parseDateRange,
    serializeDateOnlyRange,
    toDateOnlyRange,
    toDateRange,
    toInputFormats,
} from "./DatePicker.lib";
import { DatePickerMonthRangeCalendar } from "./DatePickerMonthRangeCalendar";
import { DatePickerPeriodCalendar } from "./DatePickerPeriodCalendar";
import { DatePickerShell } from "./DatePickerShell";

const getPeriodValue = (mode: PeriodSelectorProps["mode"], date: Date) => {
    if (mode === "month" || mode === "monthRange") {
        return getMonthRange(date);
    }
    if (mode === "quarter") {
        return getQuarterRange(date);
    }
    return getYearRange(date);
};

const getPeriodPlaceholder = (mode: PeriodSelectorProps["mode"]) => {
    if (mode === "month") {
        return "Select month";
    }
    if (mode === "monthRange") {
        return "Select month range";
    }
    if (mode === "quarter") {
        return "Select quarter";
    }
    return "Select year";
};

export const PeriodSelector = ({
    mode,
    value,
    onValueChange,
    presets,
    selectedPresetId,
    variant = "input",
    placeholder,
    inputFormat = DEFAULT_INPUT_FORMATS,
    displayFormat = "MMM d, yyyy",
    weekStartsOn = 0,
    ...props
}: PeriodSelectorProps) => {
    const today = useConstant(() => new Date());
    const minDate = parseDateOnly(props.minDate);
    const maxDate = parseDateOnly(props.maxDate);
    const primaryInputFormat = toInputFormats(inputFormat)[0] ?? "MM/dd/yyyy";

    return (
        <DatePickerShell
            {...props}
            value={value}
            onValueChange={onValueChange}
            selectedPresetId={selectedPresetId}
            isEqual={areDateOnlyRangesEqual}
            formatValue={(range) => formatDateRange(range, displayFormat)}
            formatValueForEditing={(range) =>
                formatDateRange(range, primaryInputFormat)
            }
            parseValue={(input) => parseDateRange(input, inputFormat)}
            serializeValue={serializeDateOnlyRange}
            renderCalendar={(range, handleChange, close) => {
                const closeWhenComplete = (isCompleteSelection: boolean) => {
                    if (
                        isCompleteSelection &&
                        !props.showActions &&
                        variant !== "inline" &&
                        variant !== "embedded"
                    ) {
                        close();
                    }
                };

                if (mode === "monthRange") {
                    return (
                        <DatePickerMonthRangeCalendar
                            value={range}
                            onValueChange={(nextRange) => {
                                handleChange(nextRange);
                                closeWhenComplete(
                                    Boolean(nextRange?.from && nextRange.to),
                                );
                            }}
                            minDate={props.minDate}
                            maxDate={props.maxDate}
                            inputFormat={inputFormat}
                            className={props.calendarClassName}
                            showAdjacentYearButtons={
                                props.showAdjacentYearButtons
                            }
                        />
                    );
                }

                return (
                    <DatePickerPeriodCalendar
                        mode={mode}
                        value={toDateRange(range)}
                        onValueChange={(nextRange: DateRange | undefined) => {
                            handleChange(toDateOnlyRange(nextRange));
                            closeWhenComplete(
                                Boolean(nextRange?.from && nextRange.to),
                            );
                        }}
                        required={props.required}
                        minDate={minDate}
                        maxDate={maxDate}
                        className={props.calendarClassName}
                    />
                );
            }}
            emptyValue={undefined}
            todayValue={toDateOnlyRange(getPeriodValue(mode, today))}
            presets={presets ?? getDefaultRangePresets(today, weekStartsOn)}
            selectionMode={mode}
            availableModes={
                props.availableModes ??
                (mode === "monthRange"
                    ? ["monthRange"]
                    : ["month", "quarter", "year"])
            }
            variant={variant}
            placeholder={placeholder ?? getPeriodPlaceholder(mode)}
            inputFormat={inputFormat}
        />
    );
};
